import { ChevronDownIcon } from '@langchain/untitled-ui-icons';

import { useId, useState } from 'react';

import { useRepoTags } from '@/hooks/useSwr';
import { cn } from '@/utils/tailwind';

import { TAGS_CATEGORY, tagsByCategory } from './constants';

const TagLine = ({
  tag,
  count,
  selected,
  toggleTag,
}: {
  tag: string;
  count: number;
  selected: boolean;
  toggleTag: (tag: string) => void;
}) => {
  const id = useId();
  return (
    <div
      key={tag}
      className={cn(
        'flex flex-1 flex-row items-center justify-stretch gap-2 self-stretch text-sm',
        count === 0 && 'opacity-40'
      )}
      title={tag}
    >
      <input
        type="checkbox"
        className="relative h-4 w-4 flex-none rounded border border-slate-200 bg-white dark:border-slate-700 dark:bg-black"
        checked={selected}
        onChange={() => toggleTag(tag)}
        id={id}
        disabled={count === 0}
      />
      <label
        htmlFor={id}
        className="flex flex-1 items-center justify-stretch gap-2 overflow-hidden"
      >
        <span
          className="shrink flex-grow overflow-hidden whitespace-nowrap text-justify"
          style={{ textOverflow: 'ellipsis' }}
        >
          {tag}
        </span>
        <span className="inline-flex h-5 flex-none shrink-0 items-center rounded bg-slate-100 px-1.5 dark:bg-slate-800">
          <span className="text-center text-xs font-medium leading-none text-slate-500 dark:text-slate-400">
            {count}
          </span>
        </span>
      </label>
    </div>
  );
};

const CategoryTagSection = ({
  category,
  icon,
  tags,
  toggleTag,
  clearTags,
}: {
  category: string;
  icon: any;
  tags: { tag: string; count: number; selected: boolean }[];
  toggleTag: (tag: string) => void;
  clearTags: () => void;
}) => {
  const [open, setOpen] = useState(true);
  const selectedInCategory = tags.filter((t) => t.selected);

  const clearable = selectedInCategory.length > 0;
  return (
    <div className="flex flex-col items-start justify-start gap-4 self-stretch">
      <div className="flex flex-col gap-1 self-stretch">
        <div
          className="inline-flex cursor-pointer items-center justify-between gap-3 self-stretch"
          onClick={() => setOpen(!open)}
        >
          <div className="flex items-center justify-start gap-2">
            <div className="relative h-4 w-4">{icon}</div>
            <div className="text-center text-sm font-medium leading-tight text-slate-900 dark:text-white">
              {category}
            </div>
          </div>
          <div className="relative h-5 w-5">
            <ChevronDownIcon className={open ? '' : 'rotate-180'} />
          </div>
        </div>
        {clearable && (
          <div className="ml-6 inline-flex h-5 items-center justify-between gap-4">
            <div className="text-xs font-normal leading-none text-slate-500 dark:text-slate-400">
              {selectedInCategory.length} selected
            </div>
            <div
              className="flex cursor-pointer items-center justify-start gap-1 rounded border border-slate-200 px-1.5 hover:bg-slate-200 dark:border-slate-700 dark:hover:bg-slate-700"
              onClick={clearTags}
            >
              <div className="text-sm font-normal leading-tight text-slate-500 dark:text-slate-400">
                Clear
              </div>
            </div>
          </div>
        )}
      </div>

      <div
        className={cn(
          'flex flex-col items-start justify-start gap-2 self-stretch',
          !open && 'hidden'
        )}
      >
        {tags.map((t) => (
          <TagLine {...t} key={t.tag} toggleTag={toggleTag} />
        ))}
      </div>
    </div>
  );
};

const TagFilterPane = ({
  selectedTags,
  setSelectedTags,
  hasCommits,
  query,
  className,
}: {
  selectedTags: string[];
  setSelectedTags: (tags: string[]) => void;
  hasCommits?: boolean;
  query?: string;
  className?: string;
}) => {
  const { data: allTags } = useRepoTags({
    has_commits: hasCommits,
    limit: 100,
    offset: 0,
  });
  const { data: tagsCount } = useRepoTags({
    has_commits: hasCommits,
    tags: selectedTags,
    query,
    limit: 100,
    offset: 0,
  });

  const allDefaultTags = tagsByCategory.flatMap((category) => category.tags);
  const innerTagsByCategory = tagsByCategory
    .map(({ category, icon, tags }) => {
      const categoryTags =
        category === TAGS_CATEGORY
          ? [
              ...tags,
              ...(tagsCount?.tags
                ?.map(({ tag }) => tag)
                .filter((t) => !allDefaultTags.includes(t)) || []),
            ]
          : tags;
      return {
        category,
        icon,
        tags: categoryTags
          .map((t) => ({
            tag: t,
            count: tagsCount?.tags?.find((t2) => t2.tag === t)?.count || 0,
            allCount: allTags?.tags?.find((t2) => t2.tag === t)?.count || 0,
          }))
          .filter((t) => t.allCount > 0)
          .sort((a, b) => a.tag.localeCompare(b.tag)),
      };
    })
    .filter(({ tags }) => tags.length > 0);

  return (
    <div
      className={cn(
        'inline-flex w-[227px] flex-col items-start gap-6 self-stretch border-l border-tertiary p-4',
        className
      )}
    >
      {innerTagsByCategory.map(({ category, icon, tags }) => (
        <CategoryTagSection
          key={category}
          category={category}
          icon={icon}
          tags={tags.map((t) => ({
            ...t,
            selected: selectedTags.includes(t.tag),
          }))}
          toggleTag={(tag) =>
            setSelectedTags(
              selectedTags.includes(tag)
                ? selectedTags.filter((t) => t !== tag)
                : [...selectedTags, tag]
            )
          }
          clearTags={() =>
            setSelectedTags(
              selectedTags.filter((t) => !tags.map((t) => t.tag).includes(t))
            )
          }
        />
      ))}
    </div>
  );
};

export default TagFilterPane;
